// Keep in sync with back-end benchling/analytics/lib/analytics_event.py

import {ActionMethod, ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';

/**
 * The possible categories for an event.
 * Any events that make a change to the core database should be `write`
 * All other events should be `read`
 */
export enum ACTION_CATEGORY {
  READ = 'read',
  WRITE = 'write',
}

// Keep in sync with benchling/analytics/lib/core/analytics_event.py
export enum ANALYTICS_MODULE {
  ANALYSIS_TOOL = 'analysis_tool',
  ANTIBODIES = 'antibodies',
  AUDIT = 'audit',
  BENCHLING_APPS = 'benchling_apps',
  CAPILLARY = 'capillary',
  CHEMISTRY = 'chemistry',
  COLLABORATION = 'collaboration',
  CONFIG_MIGRATION = 'config_migration',
  CONNECT = 'connect',
  DATA_FRAMES = 'data_frames',
  DATA_MANAGEMENT = 'data_management',
  DEEP_RESEARCH = 'deep_research',
  ELN = 'eln',
  EVENTS = 'events',
  EXTENSIONS = 'extensions',
  FORMS = 'forms',
  GROWTH = 'growth',
  IDENTITY = 'identity',
  INSIGHTS = 'external_analytics',
  INSTRUMENTS = 'instruments',
  LEGACY_WORKFLOWS = 'workflows',
  LLM_CHAT = 'llm_chat',
  MOLBIO = 'molbio',
  MODBIO = 'modified_biologics',
  MODEL_HUB = 'model_hub',
  NAVIGATION = 'navigation',
  OMNIBOX = 'omnibox',
  PERMISSION_CONTROLS = 'permission_controls',
  PIPELINES = 'pipelines',
  PROCEDURES = 'procedures',
  PROJECT_FOLDER = 'project_folder',
  REGISTRY = 'bioregistry',
  REQUESTS = 'requests',
  REQUESTS_V2 = 'requests_v2',
  RESULTS = 'results',
  REVIEWS = 'reviews',
  SAMPLES = 'samples',
  SAMPLE_TESTS = 'sample_tests',
  SCHEMAS = 'schemas',
  SIGHTGLASS = 'sightglass',
  STUDIES = 'studies',
  TENANT_COMPARISON = 'tenant_comparison',
  UNITS = 'units',
  WORKFLOWS = 'workflows_v2',
  WORKLISTS = 'worklists',
  WORKSHEETS = 'worksheets',
  // For general events, use `trackEventWithModule` to provide a specific module
  MODULE_MISSING = 'module_missing',
}

// Keep in sync with benchling/analytics/lib/core/analytics_event.py
// TODO(BNCH-52547): Replace with graphql generated enum once synced enums can have different keys/values
export enum AnalyticsModalType {
  ANTIBODY_NESTED_DATA_CREATION = 'antibody_nested_data_creation',
  BULK_UPDATE_CONCENTRATION = 'bulk_update_concentration',
  BULK_UPDATE_QUANTITY = 'bulk_update_quantity',
  BULK_UPDATE_RESTRICTED_SAMPLE_USERS = 'bulk_update_restricted_sample_users',
  BULK_UPDATE_RESTRICTION_STATUS = 'bulk_update_restriction_status',
  BULK_UPDATE_SAMPLE_OWNERS = 'bulk_update_sample_owners',
  BULK_UPDATE_FREEZE_THAW_COUNT = 'bulk_update_freeze_thaw_count',
  BULK_UPDATE_EXPIRATION_DATE_OVERRIDE = 'bulk_update_expiration_date_override',
  REACTION_SKETCHER = 'reaction_sketcher',
}

export enum ModalMetadataEntryType {
  MANUAL = 'manual',
  AUTO_POPULATED_FROM_PROCESS_TASK = 'auto_populated_from_process_task',
}

export interface ModalMetadataEntryInfo {
  entryType?: ModalMetadataEntryType;
  metadataElement?: 'location' | 'cico_assignee' | 'sample_control_role_party';
}

export interface NormalizedAnalyticsEventMetadata {
  action_origin?: ActionOrigin;
  action_method?: ActionMethod;
  // TODO(TypeScript): Add more fields
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export type AnalyticsEventMetadata = NormalizedAnalyticsEventMetadata | AnalyticsMetadataForContext;

export interface AnalyticsMetadataForContext {
  actionOrigin?: ActionOrigin;
  actionMethod?: ActionMethod;
  entryId?: string | null;
  fileId?: string | null;
  eventGroupId?: string | null;
  modalType?: string | AnalyticsModalType;
  modalMetadataEntryInfo?: Array<ModalMetadataEntryInfo>;
}
